import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { FieldArray } from "formik";
import { useDispatch } from "react-redux";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { PerformerView } from "../EventPerformers/PerformerView";
import { AssignPerformerModal } from "./AssignPerformerModal";
import { fetchPerformers } from "../../../redux/features/performersSlice";

const denormalizePerformer = (performer, order) => ({
  performerId: performer.id,
  name: performer.name ?? "",
  email: performer.email ?? "",
  links: Array.isArray(performer.links) ? performer.links : [],
  bio: performer.bio ?? "",
  credits: performer.credits ?? "",
  headshotUrl: performer.headshotUrl ?? "",
  order,
  isHeadliner: false,
});

export const lockAxisStyle = (style) => {
  if (style?.transform) {
    const axisLockY = `translate(0px, ${style.transform.split(",").pop()}`;
    return {
      ...style,
      transform: axisLockY,
    };
  }
  return style;
};

export const getItemStyle = (draggableStyle) => ({
  userSelect: "none",
  margin: `0 0 28px 0`,
  ...draggableStyle,
});

export const PerformersForm = ({
  formikProps,
  organizationPerformers,
  organizationId,
}) => {
  const dispatch = useDispatch();
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const { values, setFieldValue } = formikProps;

  const performers = values.performers || [];

  const handleAddNewPerformerSuccess = (newPerformer) => {
    if (!newPerformer?.id) return;
    const denormalized = denormalizePerformer(newPerformer, performers.length);
    setFieldValue("performers", [...performers, denormalized]);
    dispatch(fetchPerformers({ organizationId }));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newList = [...performers];
    const [removed] = newList.splice(result.source.index, 1);
    newList.splice(result.destination.index, 0, removed);
    const withOrder = newList.map((p, index) => ({ ...p, order: index }));
    setFieldValue("performers", withOrder);
  };

  const handleAddPerformer = (denormalizedPerformer) => {
    setFieldValue("performers", [...performers, denormalizedPerformer]);
    setAssignModalOpen(false);
  };

  const handleHeadlinerChange = (index, checked) => {
    const updated = performers.map((p, i) =>
      i === index ? { ...p, isHeadliner: checked } : p,
    );
    setFieldValue("performers", updated);
  };

  return (
    <Box>
      <Box
        width="100%"
        display="flex"
        gap="5px"
        sx={{ marginTop: "20px" }}
      >
        <Typography flexGrow={1}>Performers</Typography>
      </Box>
      <FieldArray name="performers">
        {({ remove }) => (
          <Box>
            <AssignPerformerModal
              open={assignModalOpen}
              onClose={() => setAssignModalOpen(false)}
              organizationId={organizationId}
              organizationPerformers={organizationPerformers}
              eventPerformers={performers}
              onAdd={handleAddPerformer}
              onAddNewPerformer={organizationId ? handleAddNewPerformerSuccess : undefined}
            />
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable-performers">
                {(provided) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ width: "100%", marginTop: "20px" }}
                  >
                    {performers.map((performer, index) => (
                      <Draggable
                        key={performer.performerId ?? index}
                        draggableId={`performer-${performer.performerId ?? index}`}
                        index={index}
                      >
                        {(providedInner, snapshot) => (
                          <div
                            ref={providedInner.innerRef}
                            {...providedInner.draggableProps}
                            style={lockAxisStyle(
                              getItemStyle(providedInner.draggableProps.style),
                            )}
                          >
                            <PerformerView
                              performer={performer}
                              handleDelete={() => remove(index)}
                              onHeadlinerChange={(checked) =>
                                handleHeadlinerChange(index, checked)
                              }
                              dhProps={providedInner.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        )}
      </FieldArray>

      <Button
        variant="outlined"
        sx={{ minWidth: 140, marginTop: "16px", textAlign: "center" }}
        onClick={() => setAssignModalOpen(true)}
      >
        Add performer
      </Button>
    </Box>
  );
};
